import type { CSSProperties, ReactElement, Ref } from 'react';
import { Children, forwardRef, useEffect, useState } from 'react';

import classNames from 'classnames';
import fastdom from 'fastdom';

import styles from './StyledOptionList.module.css';

import { visuallyHidden } from '../../../styles/visuallyHidden';
import type { StyledOptionProps } from './StyledOption';

export const DEFAULT_HEIGHT = 50;
const OFFSET_UP_VAR = 'var(--tfd-styledoptionlist-offset-up)';
const OFFSET_DOWN_VAR = 'var(--tfd-styledoptionlist-offset-down)';

/** An for specifying the rendering direction of the menu */
export const Directions = ['up', 'down'] as const;

export interface StyledOptionListProps {
  /** List of react elements that must be `StyledOption` */
  children: ReactElement<StyledOptionProps> | ReactElement<StyledOptionProps>[];
  /** The element used to position the menu */
  positionElement: HTMLElement | null;
  /** An optional key that can be used to trigger re-rendering of the list */
  positionKey?: string;
  /** A boolean indicating the open status of the list */
  isOpen: boolean;
  /** Optional class name */
  className?: string;
  /** Optional inline styles */
  style?: CSSProperties;
  /** @deprecated Direction as a prop for testing purposes */
  direction?: (typeof Directions)[number];
}

export const StyledOptionList = forwardRef(StyledOptionList_);

function StyledOptionList_(
  {
    children,
    isOpen,
    positionElement,
    positionKey,
    direction,
    style,
    className,
    ...props
  }: StyledOptionListProps,
  ref?: Ref<HTMLUListElement>
) {
  const hasListItems = Children.count(children) > 0;
  const [transform, setTransform] = useState(
    getTransform(positionElement, direction)
  );

  const classNamesList = !hasListItems
    ? visuallyHidden
    : classNames(styles.list, className, {
        [styles.isOpen]: isOpen,
      });

  useEffect(() => {
    let calculatedTransform: string;

    fastdom.measure(() => {
      calculatedTransform = getTransform(positionElement, direction);
    });

    fastdom.mutate(() => {
      if (calculatedTransform) {
        setTransform(calculatedTransform);
      }
    });
  }, [isOpen, positionKey, positionElement, direction]);

  return (
    <ul
      ref={ref}
      className={classNamesList}
      style={{ transform, ...style }}
      {...props}
    >
      {children}
    </ul>
  );
}

/**
 * Return an amount in pixels in which to transform along the Y axis relative to
 * an ancestor element.
 */
function getTransform(
  element: HTMLElement | null,
  directionProp?: (typeof Directions)[number]
): string {
  if (!element) {
    return `translateY(calc(${DEFAULT_HEIGHT}px + ${OFFSET_DOWN_VAR}))`;
  }

  const clientRect = element?.getBoundingClientRect();
  const height = clientRect.height ?? DEFAULT_HEIGHT;
  const isDown = directionProp
    ? directionProp === 'down'
    : clientRect.y < window.innerHeight / 2;

  return isDown
    ? `translateY(calc(${height}px + ${OFFSET_DOWN_VAR}))`
    : `translateY(calc(-1 * (100% + ${OFFSET_UP_VAR})))`;
}
